import React from 'react';

import Chip from '~/components/core/Atomic/Chip/Chip';
import cn from '~/Utils/cn';

/**
 * The tinted pill this tab labels state with.
 *
 * Follows the newer convention the Clive views established (see
 * `CliveInsights/views/v2/ComplexityBadge.tsx`): the Atomic `Chip` with a `bg-*-100 text-*-700`
 * pair and `h-20 rounded-full`. Deliberately **not** `StatusChipCore`, which is the older chip —
 * its `color` prop tints only a 3px left border on a fixed slate body and forces the label to
 * uppercase, so it cannot render a tinted pill at all.
 */
export const AGENT_CHIP_TONE = {
  /** An affirmative state: the conductor is on. */
  ON: 'ON',
  /** A neutral state: off, or a plain count. Grey rather than red -- off is a choice, not a fault. */
  NEUTRAL: 'NEUTRAL',
  /** Something the operator may want to come back to, e.g. a stage with no goal. */
  ATTENTION: 'ATTENTION',
  /**
   * A state that holds only sometimes — an action the agent may run once it is confident enough.
   * Its own tone rather than `ON`: the agent does not always act here, and rather than `NEUTRAL`,
   * which would read the same as never.
   */
  CONDITIONAL: 'CONDITIONAL',
  /** A sub-organization tag. */
  SUB_ORG: 'SUB_ORG',
  /** A line-of-business tag. */
  LOB: 'LOB',
} as const;

export type AgentChipTone = (typeof AGENT_CHIP_TONE)[keyof typeof AGENT_CHIP_TONE];

// Only scales the theme actually defines: `orange` and `green` exist at 100/700 only, which is
// exactly what this needs. `teal` and `blue` are full scales.
const TONE_CLASSNAMES: Record<AgentChipTone, string> = {
  [AGENT_CHIP_TONE.ON]: 'bg-green-100 text-green-700',
  [AGENT_CHIP_TONE.NEUTRAL]: 'bg-slate-300 text-slate-800',
  [AGENT_CHIP_TONE.ATTENTION]: 'bg-orange-100 text-orange-700',
  [AGENT_CHIP_TONE.CONDITIONAL]: 'bg-azure-100 text-azure-700',
  [AGENT_CHIP_TONE.SUB_ORG]: 'bg-blue-100 text-blue-600',
  [AGENT_CHIP_TONE.LOB]: 'bg-teal-100 text-teal-700',
};

export interface AgentConfigChipProps {
  label: string;
  tone: AgentChipTone;
  className?: string;
  /**
   * An optional glyph before the label, for a chip whose number needs saying what it counts —
   * the actions table's guardrail count. Tint it to match the tone; the chip does not colour it.
   */
  icon?: React.ReactElement;
}

const AgentConfigChip: React.FC<AgentConfigChipProps> = ({ label, tone, className, icon }) => (
  <Chip className={cn('h-20 rounded-full', TONE_CLASSNAMES[tone], className)} label={label} size="small" icon={icon} />
);

export default AgentConfigChip;
